import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../components/Logo";
import { confirmPasswordReset } from "../../api/authEtablissement";
import { RESET_EMAIL_KEY } from "./ForgotPassword";

const RESET_CODE_KEY = "agbaza.reset.code";

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

export default function EtablissementNewPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const e = sessionStorage.getItem(RESET_EMAIL_KEY);
    const c = sessionStorage.getItem(RESET_CODE_KEY);
    if (e) setEmail(e);
    if (c) setCode(c);
    if (!e || !c) navigate("/etablissement/mot-de-passe/oubli", { replace: true });
  }, [navigate]);

  const strong = useMemo(() => passwordRegex.test(password), [password]);
  const match = confirm === password && confirm.length > 0;
  const canSubmit = strong && match && !submitting;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || !email || !code) return;
    setError("");
    setSubmitting(true);
    const result = await confirmPasswordReset({
      email,
      code,
      otp: code,
      newPassword: password,
      confirmPassword: confirm,
    });
    setSubmitting(false);
    if (result.ok) {
      sessionStorage.removeItem(RESET_EMAIL_KEY);
      sessionStorage.removeItem(RESET_CODE_KEY);
      navigate("/etablissement/connexion", { state: { message: "Mot de passe réinitialisé. Connectez-vous." } });
    } else {
      setError(result.message || "Une erreur est survenue");
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 relative">
      {/* Mobile background image */}
      <div className="absolute inset-0 md:hidden">
        <img src={'/maps-reset-side.jpg'} alt="new-password" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/30" />
      </div>

      {/* Left: form */}
      <div className="relative flex items-center justify-center px-4 md:px-10 py-10">
        <div className="w-full max-w-md">
          <div className="flex justify-center"><Logo size={56} /></div>
          <h1 className="mt-6 text-2xl md:text-3xl font-semibold text-[#0B1F16]">Récupération du mot de passe</h1>
          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm text-[#0B1F16]">Nouveau mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={`w-full h-12 rounded-[10px] border px-3 bg-white ${strong || password === '' ? 'border-[#E5E7EB]' : 'border-red-400'}`}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[#0B1F16]">Confirmer mot de passe</label>
              <input
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                className={`w-full h-12 rounded-[10px] border px-3 bg-white ${match || confirm === '' ? 'border-[#E5E7EB]' : 'border-red-400'}`}
              />
            </div>
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-12 rounded-[10px] font-semibold cursor-pointer disabled:opacity-60"
              style={{ backgroundColor: "#00553E", color: "#FFFFFF" }}
            >
              {submitting ? "Enregistrement…" : "Confirmer"}
            </button>
            <Link to="/etablissement/connexion" className="block">
              <button type="button" className="w-full h-12 rounded-[10px] font-semibold border cursor-pointer" style={{ borderColor: "#D1E2DD", color: "#00553E" }}>
                Retour
              </button>
            </Link>
          </form>
        </div>
      </div>

      {/* Right: image (desktop) */}
      <div className="relative hidden md:block">
        <img src={'/maps-reset-side.jpg'} alt="new-password" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  );
}
